<template>
  <div class="hdc-monitor flex flex-col items-center w-full h-full">
    <!-- Header with icon -->
    <div class="card-header relative">
      <div class="relative z-10 flex flex-col items-center pb-8">
        <div
          class="flex justify-center items-center rounded-full w-[180px] h-[180px] bg-black dark:bg-black border-8 border-opacity-50"
          :class="{
            'pulse-animation': isBraking,
            'border-green-500': isActive,
            'border-gray-500': !isActive,
          }"
        >
          <div
            class="icon-glow absolute"
            :class="{ 'glow-active': isBraking }"
          ></div>
          <HillDescentControl
            width="100%"
            height="100%"
            :color="!isActive ? '#9ea5b1' : '#22c55e'"
            :isBraking="isBraking"
          />
        </div>
      </div>
    </div>

    <!-- Status section -->
    <div class="w-full status-buttons space-y-3">
      <div
        class="h-20 status-button flex items-center bg-gray-50 dark:bg-gray-700 p-4 rounded-xl transition-all duration-300 shadow-md"
      >
        <div
          class="status-indicator mr-3"
          :class="[isActive ? 'bg-green-500' : 'bg-gray-500']"
        ></div>
        <span
          class="text-xl font-bold"
          :class="[isBraking ? 'text-green-500' : 'text-gray-400']"
          >{{ showText }}</span
        >
      </div>
    </div>

    <!-- Divider with car icon -->
    <div class="w-full divider flex items-center my-4">
      <div class="h-px bg-gray-200 dark:bg-gray-600 flex-grow"></div>
      <div class="mx-4 text-gray-400 dark:text-gray-500">
        <q-icon name="speed" size="xl" />
      </div>
      <div class="h-px bg-gray-200 dark:bg-gray-600 flex-grow"></div>
    </div>

    <!-- Speed indicators -->
    <div class="w-full speed-indicators space-y-4">
      <!-- Target speed -->
      <div
        class="speed-item bg-gray-50 dark:bg-gray-700 rounded-xl p-4 shadow-sm flex justify-between items-center"
      >
        <div class="flex items-center">
          <div class="w-3 h-3 rounded-full bg-purple-500 mr-3"></div>
          <span class="text-xl font-bold">目标车速</span>
        </div>
        <div
          class="speed-value flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-full"
        >
          <span class="text-4xl font-bold text-purple-700 dark:text-purple-300">
            {{ Math.round(notifyData.UIRx_HDCDispVxTar || 0) }}
          </span>
        </div>
      </div>

      <!-- Actual speed -->
      <div
        class="speed-item bg-gray-50 dark:bg-gray-700 rounded-xl p-4 shadow-sm flex justify-between items-center"
      >
        <div class="flex items-center">
          <div class="w-3 h-3 rounded-full bg-blue-500 mr-3"></div>
          <span class="text-xl font-bold">实际车速</span>
        </div>
        <div
          class="speed-value flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full"
        >
          <span class="text-4xl font-bold text-blue-700 dark:text-blue-300">
            {{ Math.round(notifyData.UIRx_VehSpd || 0) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import HillDescentControl from "@/components/icons/HillDescentControl.vue";

// HDC激活状态图标接收UIRx_HDCCtrlA信号
// 1. 当该信号值为0时图标置灰；
// 2. 当为1时图标绿色高亮显示并闪烁；
// 3. 当为2时图标只绿色高亮显示，不闪烁

// 3. 文字"功能开启未制动"和"功能开启且正在制动"接收UIRx_HDCCtrlA信号
//  当为1时文字"功能开启且正在制动"绿色高亮显示
//  当为2时文字"功能开启未制动" 灰色显示
interface Props {
  notifyData: DomianControl.HDCNotifyData;
}

const props = withDefaults(defineProps<Props>(), {
  notifyData: () => ({} as DomianControl.HDCNotifyData),
});

const isActive = computed( // 功能开启 激活
  () => props.notifyData.UIRx_HDCCtrlA > 0 && props.notifyData.UIRx_HDCCtrlA < 3
); 
const isBraking = computed(() => props.notifyData.UIRx_HDCCtrlA === 1); // 功能开启且正在制动

const showText = computed(() => {
  if (isActive.value) {
    return isBraking.value ? "功能开启且正在制动" : "功能开启未制动";
  }
  return "功能未开启";
});
</script>

<style scoped>
.icon-circle {
  width: 180px;
  height: 180px;
}

.icon-glow {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.glow-active {
  opacity: 1;
  box-shadow: 0 0 60px 20px rgba(34, 197, 94, 0.6);
  animation: shadowExpand 2s ease-in-out infinite;
}

.pulse-animation {
  animation: pulse 2s ease-in-out infinite;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

@keyframes blink {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 1;
  }
}

@keyframes shadowExpand {
  0% {
    box-shadow: 0 0 20px 5px rgba(34, 197, 94, 0.3);
    transform: scale(0.9);
  }
  50% {
    box-shadow: 0 0 60px 20px rgba(34, 197, 94, 0.6);
    transform: scale(1);
  }
  100% {
    box-shadow: 0 0 20px 5px rgba(34, 197, 94, 0.3);
    transform: scale(0.9);
  }
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
  }
  50% {
    transform: scale(1);
  }
  100% {
    transform: scale(0.95);
  }
}

.blinking {
  animation: blink 1s ease-in-out infinite;
}
</style>
